import {
  AfterContentInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AlertController,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { globalHelper } from 'src/app/_helpers/global';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { UserBasic } from '../../../_models/user.model';
import { AuthenticationService, UserService } from '../../../_services';
import { OrderType } from '../../../_types';
import { PopoverButton } from '../popover/popover.model';
import { DateStiPipe } from '../../../_pipes/date-sti.pipe';
import { Comment } from './comment.model';
import { CommentService } from './comment.service';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
})
export class CommentComponent implements OnInit, AfterContentInit {
  @Input()
  tableName: string;

  @Input()
  id: any;

  @Input()
  userPerformedBy: number;

  @Input()
  mode: 'chat' | 'comment' = 'chat';

  @Input()
  comments: Comment[];

  order: OrderType;
  commentContent: string = '';
  showCounter = false;
  lengthMax = 2000;
  showTextarea: boolean = false;

  @Input()
  commentsCount = 0;

  @Input()
  refreshCommentsOnInit = false;

  @Input()
  deletePermission: string;

  // @Output()
  // focusStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  showComments = false;
  addingComment = false;
  showSpinnerLoading = false;
  buttonsList: PopoverButton[] = []; // tablica na wszystkie popoverButtony, które są dosepne dla każdego
  deleteButton: PopoverButton = {
    name: 'DELETE',
    icon: 'trash',
    eventType: 'delete',
  };
  hasRole: boolean;
  commentIdsToDelete: number[] = [];
  emptyCommentError: boolean = false;

  constructor(
    public commentServ: CommentService,
    public authServ: AuthenticationService,
    public translateService: TranslateService,
    public p: ThisPlatform,
    private userServ: UserService,
    private alertContr: AlertController,
    private popoverController: PopoverController,
    private toastCtrl: ToastController
  ) {}

  ngOnInit() {
    this.hasRole = this.userServ.hasRoles([this.deletePermission]);

    this.showComments = false;

    this.order = OrderType.DESC;

    if (this.mode === 'chat' || this.refreshCommentsOnInit) {
      this.loadComments(true);
      this.showComments = true;
    }

    this.showTextarea = true;
  }

  ngAfterContentInit() {
    this.loadComments(false);
    this.showComments = false;
  }

  commentsCountText() {
    const commentsNumber = this.comments
      ? this.comments.length
      : this.commentsCount;

    let text =
      String(this.comments ? this.comments.length : this.commentsCount) + ' ';

    if (commentsNumber === 0) {
      text += this.translateService.instant('COMMENTS.ZERO_COMMENT');
    } else if (commentsNumber === 1) {
      text += this.translateService.instant('COMMENTS.ONE_COMMENT');
    } else if (commentsNumber > 1 && commentsNumber < 5) {
      text += this.translateService.instant('COMMENTS.FEW_COMMENT');
    } else if (commentsNumber > 4) {
      text += this.translateService.instant('COMMENTS.MANY_COMMENT');
    }

    return text;
  }

  loadComments(isShowingComments: boolean) {
    this.showSpinnerLoading = true;
    this.commentServ
      .getCommentsFilterBy(this.tableName, this.id, this.order)
      .subscribe((comments: Comment[]) => {
        this.comments = comments.filter((e) => {
          if (!this.commentIdsToDelete.includes(e.id)) {
            return e;
          }
        });
        this.showSpinnerLoading = false;
        this.showComments = isShowingComments;
      });
  }

  Save() {
    if (this.commentContent.trim().length > 0) {
      this.emptyCommentError = false;
      this.addingComment = true;
      this.commentServ
        .createComment(this.tableName, this.id, this.commentContent)
        .subscribe((res) => {
          this.commentServ
            .getCommentsFilterBy(this.tableName, this.id, this.order)
            .subscribe((comments: Comment[]) => {
              this.addingComment = false;
              this.comments = comments.filter((e) => {
                if (!this.commentIdsToDelete.includes(e.id)) {
                  return e;
                }
              });
              this.commentContent = '';
            });
        });
    } else {
      this.emptyCommentError = true;
    }
  }

  getUserInitials(user: UserBasic) {
    return this.userServ.getShortUserName(user.firstName + ' ' + user.lastName);
  }

  async deleteComment(com: Comment) {
    const temporaryComms = this.comments;
    const stiDate = new DateStiPipe(this.userServ);
    this.commentIdsToDelete.push(com.id);
    let message =
      this.translateService.instant('COMMENTS.DELETE_COMMENT_INFO') +
      stiDate.transform(com.createdAt) +
      this.translateService.instant('COMMENTS.DELETE_COMMENT_INFO_CD');

    if (com.content.length < 70) {
      message += com.content;
    } else {
      message += com.content.substr(0, 70);
      message += '...';
    }
    const confirmDelete = await globalHelper.Alert(
      this.alertContr,
      this.translateService,
      message
    );

    if (confirmDelete) {
      this.comments = this.comments.filter((c) => c.id !== com.id);
      const toast = await this.toastCtrl.create({
        header: this.translateService.instant('COMMENTS.COMMENT_REMOVED'),
        message: this.translateService.instant('COMMENTS.RESTORE'),
        position: 'bottom',
        buttons: [
          {
            side: 'end',
            icon: 'arrow-undo-outline',
            text: this.translateService.instant('UNDO'),
            role: 'undo',
            handler: () => {},
          },
          {
            side: 'end',
            icon: 'close-outline',
            handler: () => {},
          },
        ],
        cssClass: 'undo-delete-comment-toast',
        duration: 5000,
      });
      await toast.present();
      const { role } = await toast.onDidDismiss();
      if (role === 'timeout' || !role) {
        this.commentServ.deleteComment(com.id).subscribe((e: Comment) => {
          this.loadComments(false);
        });
      } else {
        this.comments = temporaryComms;
      }
    }
    // usuwanie id komentarza z tablicy commentIdsToDelete ma na celu zwolnienie pamięci,
    // po anulowaniu usuwania poprawne wyswietlanie komentarza,
    // zoptymalizowanie filtrowania poprzez ograniczenie ilości elementów w tablicy
    this.commentIdsToDelete.splice(
      this.commentIdsToDelete.findIndex((e) => e == com.id),
      1
    );
  }

  async presentPopover(event: Event, item: Comment) {
    const buttonsList: PopoverButton[] = this.buttonsList.slice();
    if (this.canDelete(item.user.id)) {
      buttonsList.push(this.deleteButton);
    }
    const popover = await globalHelper.Popover(
      this.popoverController,
      event,
      buttonsList
    );
    if (popover) {
      if (popover.data === 'delete') {
        this.deleteComment(item);
      }
    }
  }
  canDelete(idUser: number, automaticallyGenerated: boolean = false): boolean {
    if (automaticallyGenerated) {
      return false;
    }
    if (this.userServ.userStorage.id === idUser) {
      return true;
    }
    return this.hasRole;
  }
  // /**
  //  *Odpowiada za obsługę eventów onfocus i onblur
  //  *
  //  * @param {boolean} focus Przyjmuje wrtość true dla onfocus, dla onblur false
  //  * @memberof CommentComponent
  //  */
  // onFocusStateChange(focus: boolean) {
  //   this.showCounter = focus;
  //   this.focusStateChanged.emit(focus);
  // }
}
